import type { FilterValue } from "@repo/design-system/types/filter";
import type { LocationOptionTreeNode } from "@repo/design-system/types/filtermenu";

/**
 * Takes an array of checked nodes and a node tree,
 * and then tries to collapse the checked nodes
 * into the closest common ancestor that has all
 * children checked.
 *
 * The NestedOptionsList only cares about the "leafs".
 * In the past this based on checkModel="leafs" in react-checkbox-tree.
 * Now that we've replaced this component we've copied the behavior.
 * I.e. when clicking on an option what has children, all children are
 * considered checked not the option itself.
 *
 * The benefit of this approach is that it that it means, clicking on
 * all children individually is the same as clicking on the parent node.
 */
export function rootify(
  checked: string[] = [],
  optionsTree: LocationOptionTreeNode[] = []
) {
  const newChecked: string[] = [];
  // Returns true if the sub tree is fully checked
  // If the sub tree is not fully check, the nodes are added directly
  const traverse = (nodes: LocationOptionTreeNode[]) => {
    const checkedChildren = nodes.filter((node) => {
      if (checked.includes(node.value)) {
        return true;
      }
      if (node.children) {
        return traverse(node.children);
      }
      return false;
    });
    if (checkedChildren.length === nodes.length) {
      return true;
    }
    newChecked.push(...checkedChildren.map((node) => node.value));
  };
  if (traverse(optionsTree)) {
    newChecked.push(...optionsTree.map((node) => node.value));
  }
  return newChecked;
}
/**
 * Takes an array of checked nodes and a node tree,
 * and then resolves all checked nodes to their
 * children. So only the children of a checked node
 * end up in the final list.
 *
 * This is the equivalent of the rootify() function. See rootify() for
 * more details on the motivation behind this.
 */
export function leafify(
  checked: FilterValue[] = [],
  optionsTree: LocationOptionTreeNode[] = []
) {
  const newChecked: FilterValue[] = [];
  const traverse = (node: LocationOptionTreeNode, addLeafs: boolean) => {
    // A node higer up was already check or this node is checked
    const newAddLeafs = addLeafs || checked.includes(node.value);
    if (!node.children && newAddLeafs) {
      // The node is a leaf and should be added
      newChecked.push(node.value);
    }
    if (node.children) {
      for (const child of node.children) {
        traverse(child, newAddLeafs);
      }
    }
  };
  for (const node of optionsTree) {
    traverse(node, false);
  }
  return newChecked;
}
